import { useEffect, useMemo, useState } from "react";
import { Shirt, Lock, Save, Loader2, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader, PrimaryButton, SecondaryButton, Card, Field } from "@/components/design-system";

/* 3 preset kalıp tipi — KEYS locked. Order matters for UI: Dar → Normal → Bol. */
const FIT_PRESETS = [
    {
        key: "slim",
        title: "Dar Kalıp",
        subtitle: "slim / dar",
        badge: "Ürün olduğundan dar keser",
    },
    {
        key: "normal",
        title: "Normal Kalıp",
        subtitle: "normal",
        badge: "Ürün olduğu gibi keser",
    },
    {
        key: "relaxed",
        title: "Bol Kalıp",
        subtitle: "relaxed / bol / oversize",
        badge: "Ürün olduğundan bol keser",
    },
];

const DEFAULTS = {
    slim:    { label: "Dar Kalıp",    advice: "Bir beden büyük almanızı öneririz." },
    normal:  { label: "Normal Kalıp", advice: "Kendi bedeninizi alabilirsiniz." },
    relaxed: { label: "Bol Kalıp",    advice: "Bir beden küçük almanızı öneririz." },
};

/**
 * KATALOG > YAPILANDIRMA > KALIP BİLGİLERİ
 *
 * 3 kilitli preset (slim/normal/relaxed) — KEYS immutable (Bedenimi Bul
 * algoritması bunlara bağlı). Kullanıcı yalnızca etiket + öneri metnini
 * düzenleyebilir. Ekle/sil YOK.
 */
export default function FitInfoLibrary() {
    const { fitLabels, updateFitLabels, loaded } = useSizing();
    const [draft, setDraft] = useState(() => ({ ...DEFAULTS }));
    const [saving, setSaving] = useState(false);

    // Hydrate draft when store's fitLabels arrive (or refreshes)
    useEffect(() => {
        if (fitLabels) setDraft({ ...DEFAULTS, ...fitLabels });
    }, [fitLabels]);

    const server = fitLabels || DEFAULTS;
    const isDirty = useMemo(() => {
        for (const p of FIT_PRESETS) {
            const s = server[p.key] || DEFAULTS[p.key];
            const d = draft[p.key] || DEFAULTS[p.key];
            if ((s.label || "") !== (d.label || "")) return true;
            if ((s.advice || "") !== (d.advice || "")) return true;
        }
        return false;
    }, [draft, server]);

    const setField = (key, field, value) => {
        setDraft((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    };

    const resetOne = (key) => {
        setDraft((prev) => ({ ...prev, [key]: { ...DEFAULTS[key] } }));
    };

    const resetAll = () => {
        setDraft({ ...DEFAULTS });
    };

    const save = async () => {
        // Client-side pre-validation to fail fast before server round-trip
        for (const p of FIT_PRESETS) {
            const d = draft[p.key] || {};
            if (!d.label || !d.label.trim()) {
                toast.error(`${p.title} için etiket boş olamaz`);
                return;
            }
            if (!d.advice || !d.advice.trim()) {
                toast.error(`${p.title} için öneri metni boş olamaz`);
                return;
            }
        }
        setSaving(true);
        try {
            const payload = {};
            for (const p of FIT_PRESETS) {
                payload[p.key] = {
                    label: draft[p.key].label.trim(),
                    advice: draft[p.key].advice.trim(),
                };
            }
            await updateFitLabels(payload);
            toast.success("Kalıp bilgileri kaydedildi");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="fit-info-library-page">
            <PageHeader
                icon={Shirt}
                title="Kalıp Bilgileri"
                description="3 kalıp tipinin etiket ve öneri metinlerini düzenle. Widget bu metinleri ürünün kalıp tipine göre gösterir."
            />

            {/* Info banner — kilit gerekçesi */}
            <div className="rounded-xl border border-border bg-muted/30 p-4 flex items-start gap-3 text-sm" data-testid="fit-info-lock-banner">
                <Lock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <div className="text-muted-foreground leading-relaxed">
                    Kalıp tipleri <span className="font-medium text-foreground">kilitlidir</span> — yalnızca 3 varyasyon vardır: <span className="font-medium">dar, normal, bol</span>. Yeni kalıp eklenemez veya silinemez, çünkü Bedenimi Bul algoritması bu üç tipe göre çalışır. Sadece <span className="font-medium">metinleri</span> özelleştirebilirsiniz.
                </div>
            </div>

            {loaded ? null : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid="fit-info-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...
                </div>
            )}

            {/* 3 preset cards */}
            {loaded && (
                <div className="grid gap-4 md:grid-cols-3" data-testid="fit-info-cards">
                    {FIT_PRESETS.map((p) => {
                        const d = draft[p.key] || DEFAULTS[p.key];
                        const isDefault = d.label === DEFAULTS[p.key].label && d.advice === DEFAULTS[p.key].advice;
                        return (
                            <Card key={p.key} className="p-5 space-y-4" data-testid={`fit-card-${p.key}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                                <Lock className="h-2.5 w-2.5" /> {p.subtitle}
                                            </span>
                                        </div>
                                        <h3 className="font-display text-lg font-semibold mt-2 tracking-tight">{p.title}</h3>
                                        <p className="text-xs text-muted-foreground mt-0.5">{p.badge}</p>
                                    </div>
                                    {!isDefault && (
                                        <button
                                            type="button"
                                            onClick={() => resetOne(p.key)}
                                            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"
                                            data-testid={`fit-reset-${p.key}`}
                                            title="Bu kalıbı varsayılana döndür"
                                        >
                                            <RotateCcw className="h-3 w-3" /> Sıfırla
                                        </button>
                                    )}
                                </div>

                                <Field
                                    label="Etiket"
                                    hintRight
                                    hint={`${(d.label || "").length}/40`}
                                >
                                    <Input
                                        value={d.label}
                                        onChange={(e) => setField(p.key, "label", e.target.value)}
                                        placeholder={DEFAULTS[p.key].label}
                                        maxLength={40}
                                        className="text-sm"
                                        data-testid={`fit-label-input-${p.key}`}
                                    />
                                    <p className="text-[10px] text-muted-foreground/70">
                                        Widget&apos;ta ürünün kalıp adı olarak görünür.
                                    </p>
                                </Field>

                                <Field
                                    label="Öneri Metni"
                                    hintRight
                                    hint={`${(d.advice || "").length}/140`}
                                >
                                    <Textarea
                                        value={d.advice}
                                        onChange={(e) => setField(p.key, "advice", e.target.value)}
                                        placeholder={DEFAULTS[p.key].advice}
                                        maxLength={140}
                                        rows={3}
                                        className="text-sm resize-none"
                                        data-testid={`fit-advice-input-${p.key}`}
                                    />
                                    <p className="text-[10px] text-muted-foreground/70">
                                        Etiketin yanında ziyaretçiye gösterilir.
                                    </p>
                                </Field>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Sticky action bar */}
            {loaded && (
                <div className="flex items-center justify-between gap-3 border-t border-border pt-4" data-testid="fit-info-action-bar">
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <Info className="h-3.5 w-3.5" />
                        <span>
                            {isDirty ? "Kaydedilmemiş değişiklikler var." : "Değişiklik yok — kaydedildi."}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <SecondaryButton size="sm" data-testid="fit-reset-all-btn">
                                    <RotateCcw className="h-3.5 w-3.5" /> Tümünü Sıfırla
                                </SecondaryButton>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="fit-reset-confirm-dialog">
                                <AlertDialogHeader>
                                    <AlertDialogTitle>3 Kalıp Da Varsayılana Dönsün Mü?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Şu anki metinler Sayz varsayılan metinleriyle değiştirilir. Bu değişiklik henüz kaydedilmez — üstte &quot;Kaydet&quot; ile onaylamayı unutmayın.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                                    <AlertDialogAction onClick={resetAll} data-testid="fit-reset-confirm">Evet, Varsayılana Dön</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                        <PrimaryButton onClick={save} disabled={!isDirty || saving} data-testid="fit-save-btn">
                            {saving ? (
                                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kaydediliyor…</>
                            ) : (
                                <><Save className="h-3.5 w-3.5" /> Kaydet</>
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </div>
    );
}
